import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { authAPI, shipmentsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import axios from 'axios';
import { 
  FiTruck, 
  FiPackage, 
  FiEdit, 
  FiSave,
  FiX,
  FiPlus,
  FiSearch,
  FiDownload,
  FiUpload,
  FiFileText,
  FiCheckCircle,
  FiAlertCircle,
  FiAlertTriangle,
  FiChevronDown,
  FiChevronRight,
  FiBox,
  FiUser,
  FiMapPin,
  FiEye,
  FiXCircle
} from 'react-icons/fi';

const AdminShipments = () => {
  const [editingShipment, setEditingShipment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showTrackingUploadModal, setShowTrackingUploadModal] = useState(false);
  const [showLabelUploadModal, setShowLabelUploadModal] = useState(false);
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [trackingFile, setTrackingFile] = useState(null);
  const [labelFile, setLabelFile] = useState(null);
  const [selectedMasterShipment, setSelectedMasterShipment] = useState(null);
  const [expandedChildShipments, setExpandedChildShipments] = useState(new Set());
  const [expandedShipments, setExpandedShipments] = useState(new Set());
  const [showProceedModal, setShowProceedModal] = useState(false);
  const [selectedShipmentForProceed, setSelectedShipmentForProceed] = useState(null);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [selectedShipmentForReceive, setSelectedShipmentForReceive] = useState(null);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [selectedShipmentForDispatch, setSelectedShipmentForDispatch] = useState(null);
  const [showDeliverModal, setShowDeliverModal] = useState(false);
  const [selectedShipmentForDeliver, setSelectedShipmentForDeliver] = useState(null);
  const [labelUploadType, setLabelUploadType] = useState('master'); // 'master', 'unit'
  const [selectedUnitForLabel, setSelectedUnitForLabel] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('waiting-labels'); // 'waiting-labels', 'ready-package', 'origin-warehouse', 'destination-warehouse', 'delivered'
  
  // Child shipment cancellation state
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [cancellationForm, setCancellationForm] = useState({
    child_shipment_id: null,
    reason: '',
    customReason: ''
  });
  
  // Warehouse functionality
  const [showBoxScanModal, setShowBoxScanModal] = useState(false);
  const [showBoxTrackingModal, setShowBoxTrackingModal] = useState(false);
  const [boxId, setBoxId] = useState('');
  const [warehouseType, setWarehouseType] = useState('origin'); // 'origin' or 'destination'
  const [boxTrackingData, setBoxTrackingData] = useState([]);
  const queryClient = useQueryClient();

  // Get all shipments for tab counts (always use pending shipments API)
  const { data: allShipments } = useQuery(
    'all-admin-shipments',
    () => authAPI.getPendingShipments(),
    {
      enabled: true,
      refetchOnWindowFocus: false,
    }
  );

  // Get specific data for current tab
  const { data: shipments, isLoading } = useQuery(
    ['admin-shipments', activeTab],
    () => {
      console.log('QUERY DEBUG: Fetching data for tab:', activeTab);
      if (activeTab === 'origin-warehouse') {
        return shipmentsAPI.getOriginWarehouseShipments();
      } else if (activeTab === 'destination-warehouse') {
        return shipmentsAPI.getDestinationWarehouseShipments();
      } else if (activeTab === 'delivered') {
        // Use pending shipments API which now includes delivered status
        return authAPI.getPendingShipments();
      } else {
        return authAPI.getPendingShipments();
      }
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        console.log('QUERY DEBUG: Data loaded for tab', activeTab, ':', data);
      }
    }
  );

  const updateMutation = useMutation(
    ({ shipmentId, data }) => authAPI.updateShipmentTracking(shipmentId, data),
    {
      onSuccess: () => {
        toast.success('Shipment updated successfully');
        queryClient.invalidateQueries(['admin-shipments', activeTab]);
        queryClient.invalidateQueries('all-admin-shipments');
        setEditingShipment(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Update failed');
      },
    }
  );

  // Warehouse mutations
  const scanBoxMutation = useMutation(
    ({ boxId, warehouseType }) => {
      // For origin warehouse, send box_id; for destination warehouse, send box_tracking_number
      const requestData = warehouseType === 'origin' 
        ? { box_id: boxId, warehouse_type: warehouseType }
        : { box_tracking_number: boxId, warehouse_type: warehouseType };
      
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ship.gxpresss.com/api';
      const token = localStorage.getItem('token');
      
      return axios.post(`${API_BASE_URL}/shipments/warehouse/scan-box/`, requestData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        }
      }).then(res => res.data);
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['admin-shipments', activeTab]);
        queryClient.invalidateQueries('all-admin-shipments');
        toast.success(data.message);
        setShowBoxScanModal(false);
        setBoxId('');
      },
      onError: (error) => {
        toast.error('Failed to scan box');
        console.error('Error:', error);
      }
    }
  );

  const uploadBoxTrackingMutation = useMutation(
    (boxesData) => shipmentsAPI.uploadBoxTrackingNumbers(boxesData),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['admin-shipments', activeTab]);
        queryClient.invalidateQueries('all-admin-shipments');
        toast.success(data.message);
        setShowBoxTrackingModal(false);
        setBoxTrackingData([]);
      },
      onError: (error) => {
        toast.error('Failed to upload tracking numbers');
        console.error('Error:', error);
      }
    }
  );

  // Receive shipment mutation (Destination Warehouse)
  const receiveShipmentMutation = useMutation(
    ({ shipmentId, data }) => authAPI.updateShipmentTracking(shipmentId, data),
    {
      onSuccess: () => {
        toast.success('Shipment received at destination warehouse!');
        queryClient.invalidateQueries(['admin-shipments', activeTab]);
        queryClient.invalidateQueries('all-admin-shipments');
        setShowReceiveModal(false);
        setSelectedShipmentForReceive(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to receive shipment');
      },
    }
  );

  // Dispatch shipment mutation
  const dispatchShipmentMutation = useMutation(
    ({ shipmentId, data }) => {
      console.log('DISPATCH DEBUG: Sending data:', { shipmentId, data });
      return authAPI.updateShipmentTracking(shipmentId, data);
    },
    {
      onSuccess: (response) => {
        console.log('DISPATCH DEBUG: Success response:', response);
        toast.success('Shipment dispatched! Now in transit to customers.');
        queryClient.invalidateQueries(['admin-shipments', activeTab]);
        queryClient.invalidateQueries('all-admin-shipments');
        setShowDispatchModal(false);
        setSelectedShipmentForDispatch(null);
      },
      onError: (error) => {
        console.log('DISPATCH DEBUG: Error:', error);
        toast.error(error.response?.data?.error || 'Failed to dispatch shipment');
      },
    }
  );

  // Deliver shipment mutation
  const deliverShipmentMutation = useMutation(
    ({ shipmentId, data }) => authAPI.updateShipmentTracking(shipmentId, data),
    {
      onSuccess: () => {
        toast.success('Shipment marked as delivered!');
        queryClient.invalidateQueries(['admin-shipments', activeTab]);
        queryClient.invalidateQueries('all-admin-shipments');
        setShowDeliverModal(false);
        setSelectedShipmentForDeliver(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to mark as delivered');
      },
    }
  );

  const deliverChildShipmentMutation = useMutation(
    ({ shipmentId, childShipmentId, data }) => authAPI.updateShipmentTracking(shipmentId, data),
    {
      onSuccess: () => {
        toast.success('Child shipment marked as delivered!');
        queryClient.invalidateQueries(['admin-shipments', activeTab]);
        queryClient.invalidateQueries('all-admin-shipments');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to mark child shipment as delivered');
      },
    }
  );

  // Cancel child shipment mutation
  const cancelChildShipmentMutation = useMutation(
    ({ childShipmentId, reason }) => shipmentsAPI.cancelChildShipment(childShipmentId, reason),
    {
      onSuccess: (response) => {
        toast.success('Child shipment cancelled successfully!');
        setShowCancellationModal(false);
        setCancellationForm({ child_shipment_id: null, reason: '', customReason: '' });
        // Refresh shipments data
        queryClient.invalidateQueries(['admin-shipments', activeTab]);
        queryClient.invalidateQueries('all-admin-shipments');
      },
      onError: (error) => {
        console.error('Cancellation error:', error);
        toast.error(error.response?.data?.error || 'Failed to cancel child shipment');
      }
    }
  );

  const handleEdit = (shipment) => {
    setEditingShipment({
      ...shipment,
      boxes: shipment.boxes || []
    });
  };

  const handleSave = () => {
    if (!editingShipment) return;

    const updateData = {
      status: editingShipment.status,
      admin_notes: editingShipment.admin_notes,
      boxes: editingShipment.boxes.map(box => ({
        id: box.id,
        carrier_tracking_number: box.carrier_tracking_number,
        carrier: box.carrier,
        status: box.status,
        weight: box.weight,
        dimensions: box.dimensions,
        admin_notes: box.admin_notes,
        units: box.units?.map(unit => ({
          id: unit.id,
          carrier_tracking_number: unit.carrier_tracking_number,
          status: unit.status,
          admin_notes: unit.admin_notes,
        })) || []
      }))
    };

    updateMutation.mutate({
      shipmentId: editingShipment.id,
      data: updateData
    });
  };

  const handleBoxChange = (boxIndex, field, value) => {
    setEditingShipment(prev => ({
      ...prev,
      boxes: prev.boxes.map((box, index) => 
        index === boxIndex ? { ...box, [field]: value } : box
      )
    }));
  };

  const handleUnitChange = (boxIndex, unitIndex, field, value) => {
    setEditingShipment(prev => ({
      ...prev,
      boxes: prev.boxes.map((box, index) => 
        index === boxIndex ? {
          ...box,
          units: box.units?.map((unit, uIndex) => 
            uIndex === unitIndex ? { ...unit, [field]: value } : unit
          ) || []
        } : box
      )
    }));
  };

  // CSV Export Functions
  const exportShipmentsCSV = () => {
    const shipmentsToExport = selectedShipments.length > 0 
      ? filteredShipments.filter(s => selectedShipments.includes(s.id))
      : filteredShipments;

    // Create a flat structure with each child shipment as a separate row
    const csvRows = [];
    
    // Add header row
    csvRows.push([
      'Master Shipment ID',
      'Master Tracking Number', 
      'Master Status',
      'User Company',
      'User Username',
      'Origin Address',
      'Destination Address',
      'Master Weight',
      'Master Created At',
      'Unit ID (Child Shipment ID)',
      'Unit Tracking Number',
      'Unit Carrier Tracking Number',
      'Unit Carrier',
      'Unit Status',
      'Item Description',
      'Quantity',
      'Recipient Name',
      'Recipient Address',
      'Address Line 1',
      'Address Line 2',
      'City',
      'State',
      'Zip Code',
      'Country',
      'Recipient Phone',
      'Recipient Email',
      'Unit Weight (gms)',
      'Item Value',
      'HSN Code',
      'Length (cm)',
      'Width (cm)',
      'Height (cm)',
      'Reference ID',
      'Admin Notes'
    ].join(','));

    // Add data rows - using new simplified structure with units directly under master shipments
    shipmentsToExport.forEach(shipment => {
      if (shipment.units && shipment.units.length > 0) {
        // Create a row for each unit directly under the master shipment
        shipment.units.forEach(unit => {
          csvRows.push([
            shipment.id,
            shipment.master_tracking_number || '',
            shipment.status,
            shipment.user?.company_name || '',
            shipment.user?.username || '',
            `"${shipment.origin_address || ''}"`,
            `"${shipment.destination_address || ''}"`,
            shipment.total_weight || '',
            shipment.created_at,
            unit.id, // This is the unit ID that will be used for tracking number upload
            unit.child_tracking_number || '',
            unit.carrier_tracking_number || '',
            unit.carrier || '',
            unit.status,
            `"${unit.item_description || ''}"`,
            1, // quantity
            `"${unit.customer_name || ''}"`,
            `"${unit.address_line1 || ''}"`,
            `"${unit.address_line1 || ''}"`,
            `"${unit.address_line2 || ''}"`,
            `"${unit.city || ''}"`,
            `"${unit.state || ''}"`,
            unit.zip || '',
            `"${unit.country || ''}"`,
            unit.customer_phone || '',
            unit.customer_email || '',
            unit.weight_gms || '',
            unit.item_value || '',
            unit.hsn_code || '',
            unit.length_cm || '',
            unit.width_cm || '',
            unit.height_cm || '',
            unit.reference_id || '',
            `"${unit.admin_notes || ''}"`
          ].join(','));
        });
      } else {
        // If no units, create a single row with master shipment data only
        csvRows.push([
          shipment.id,
          shipment.master_tracking_number || '',
          shipment.status,
          shipment.user?.company_name || '',
          shipment.user?.username || '',
          `"${shipment.origin_address || ''}"`,
          `"${shipment.destination_address || ''}"`,
          shipment.total_weight || '',
          shipment.created_at,
          '', // No unit ID
          '', // No tracking number
          '', // No carrier tracking number
          '', // No carrier
          '', // No status
          '', // No item description
          '', // No quantity
          '', // No customer name
          '', // No address
          '', // No address line 1
          '', // No address line 2
          '', // No city
          '', // No state
          '', // No zip
          '', // No country
          '', // No phone
          '', // No email
          '', // No weight
          '', // No item value
          '', // No HSN code
          '', // No length
          '', // No width
          '', // No height
          '', // No reference ID
          ''  // No admin notes
        ].join(','));
      }
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `shipments_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${shipmentsToExport.length} master shipments with all child data!`);
  };

  // Tracking Number Upload
  const handleTrackingFileUpload = (event) => {
    const file = event.target.files[0];
    console.log('File selected:', file);
    console.log('File name:', file?.name);
    console.log('File type:', file?.type);
    console.log('File size:', file?.size);
    
    if (file) {
      // Check if file is CSV by extension or MIME type
      const isCSV = file.name.toLowerCase().endsWith('.csv') || 
                   file.type === 'text/csv' || 
                   file.type === 'application/csv' ||
                   file.type === 'text/plain' ||
                   file.type === '';
      
      console.log('Is CSV:', isCSV);
      
      if (isCSV) {
        setTrackingFile(file);
        toast.success('CSV file selected successfully');
      } else {
        toast.error(`Please select a valid CSV file. File type: ${file.type}, Extension: ${file.name.split('.').pop()}`);
      }
    } else {
      toast.error('Please select a file');
    }
  };

  // Download sample tracking CSV template
  const downloadTrackingTemplate = () => {
    // Get actual unit IDs from current shipments using new structure
    const actualIds = [];
    shipments?.forEach(shipment => {
      if (shipment.units) {
        shipment.units.forEach(unit => {
          actualIds.push({
            child_shipment_id: unit.id, // Use database ID with correct column name
            tracking_number: '1Z999AA1234567890', // Sample tracking number
            carrier: 'UPS'
          });
        });
      }
    });
    
    // Use actual IDs if available, otherwise use sample data
    const sampleData = actualIds.length > 0 ? actualIds.slice(0, 5) : [
      {
        child_shipment_id: '1', // Use database ID with correct column name
        tracking_number: '1Z999AA1234567890',
        carrier: 'UPS'
      },
      {
        child_shipment_id: '2', // Use database ID with correct column name
        tracking_number: '1Z999BB1234567891',
        carrier: 'UPS'
      },
      {
        child_shipment_id: '3', // Use database ID with correct column name
        tracking_number: '999BB1234567891',
        carrier: 'USPS'
      }
    ];

    const headers = Object.keys(sampleData[0]);
    const csvContent = [
      headers.join(','),
      ...sampleData.map(row => headers.map(header => row[header]).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'tracking_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Tracking template downloaded!');
  };

  const uploadTrackingNumbers = async () => {
    if (!trackingFile) {
      toast.error('Please select a CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('file', trackingFile);

    try {
      const response = await shipmentsAPI.uploadTrackingNumbers(formData);
      console.log('Upload response:', response);
      
      if (response.message || response.updated_count !== undefined) {
        toast.success(`Successfully updated ${response.updated_count} tracking numbers`);
        if (response.errors && response.errors.length > 0) {
          toast.warning(`${response.errors.length} rows had errors: ${response.errors.join(', ')}`);
        }
        queryClient.invalidateQueries(['admin-shipments', activeTab]);
        queryClient.invalidateQueries('all-admin-shipments');
        setShowTrackingUploadModal(false);
        setTrackingFile(null);
      } else {
        toast.error(response.error || 'Failed to upload tracking numbers');
      }
    } catch (error) {
      console.error('Error uploading tracking numbers:', error);
      const errorMessage = error.response?.data?.error || 'Failed to upload tracking numbers';
      toast.error(errorMessage);
    }
  };

  // Label Upload
  const handleLabelFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setLabelFile(file);
    } else {
      toast.error('Please select a valid PDF file');
    }
  };

  const uploadLabel = async () => {
    if (!labelFile) {
      toast.error('Please select a PDF file');
      return;
    }

    let formData = new FormData();
    formData.append('file', labelFile);

    try {
      let response;
      
      if (labelUploadType === 'master') {
        if (!selectedMasterShipment) {
          toast.error('Please select a master shipment');
          return;
        }
        formData.append('master_shipment_id', selectedMasterShipment.id);
        response = await shipmentsAPI.uploadMasterLabel(formData);
      } else if (labelUploadType === 'unit') {
        if (!selectedUnitForLabel) {
          toast.error('Please select a unit');
          return;
        }
        formData.append('unit_id', selectedUnitForLabel.id);
        response = await shipmentsAPI.uploadUnitLabel(formData);
      }

      console.log('Label upload response:', response);
      toast.success('Label uploaded successfully!');
      
      // Invalidate all relevant queries to refresh the data
      queryClient.invalidateQueries(['admin-shipments', activeTab]);
      queryClient.invalidateQueries('all-admin-shipments');
      queryClient.invalidateQueries('admin-pending-shipments');
      
      setShowLabelUploadModal(false);
      setLabelFile(null);
      setSelectedMasterShipment(null);
      setSelectedUnitForLabel(null);
    } catch (error) {
      console.error('Error uploading label:', error);
      const errorMessage = error.response?.data?.error || 'Failed to upload label';
      toast.error(errorMessage);
    }
  };

  // Proceed shipment function
  const handleProceed = (shipment) => {
    setSelectedShipmentForProceed(shipment);
    setShowProceedModal(true);
  };

  // Receive shipment function (Destination Warehouse)
  const handleReceive = (shipment) => {
    setSelectedShipmentForReceive(shipment);
    setShowReceiveModal(true);
  };

  // Dispatch shipment function
  const handleDispatch = (shipment) => {
    setSelectedShipmentForDispatch(shipment);
    setShowDispatchModal(true);
  };

  // Deliver shipment function
  const handleDeliver = (shipment) => {
    setSelectedShipmentForDeliver(shipment);
    setShowDeliverModal(true);
  };

  const handleDeliverChildShipment = (shipment, childShipment) => {
    console.log('DEBUG: Delivering child shipment:', childShipment.id, 'from shipment:', shipment.id);
    console.log('DEBUG: Shipment data structure:', shipment);
    
    // Handle both data structures: boxes.units (warehouse tabs) and units (delivered tab)
    let updateData;
    
    if (shipment.boxes && shipment.boxes.length > 0) {
      // Warehouse tabs structure: boxes.units
      updateData = {
        boxes: shipment.boxes.map(box => ({
          id: box.id,
          units: box.units?.map(unit => ({
            id: unit.id,
            status: unit.id === childShipment.id ? 'delivered' : unit.status
          })) || []
        }))
      };
    } else if (shipment.units && shipment.units.length > 0) {
      // Delivered tab structure: units directly
      updateData = {
        units: shipment.units.map(unit => ({
          id: unit.id,
          status: unit.id === childShipment.id ? 'delivered' : unit.status
        }))
      };
    } else {
      console.error('DEBUG: No boxes or units found in shipment data');
      return;
    }
    
    console.log('DEBUG: Update data:', updateData);
    
    deliverChildShipmentMutation.mutate({
      shipmentId: shipment.id,
      childShipmentId: childShipment.id,
      data: updateData
    });
  };

  // Handle child shipment cancellation
  const handleCancelChildShipment = (childShipmentId) => {
    setCancellationForm({
      child_shipment_id: childShipmentId,
      reason: '',
      customReason: ''
    });
    setShowCancellationModal(true);
  };

  // Submit cancellation
  const handleSubmitCancellation = () => {
    if (!cancellationForm.reason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }

    if (cancellationForm.reason === 'other' && !cancellationForm.customReason?.trim()) {
      toast.error('Please provide details for the cancellation reason');
      return;
    }

    const finalReason = cancellationForm.reason === 'other' 
      ? cancellationForm.customReason.trim() 
      : cancellationForm.reason;

    cancelChildShipmentMutation.mutate({
      childShipmentId: cancellationForm.child_shipment_id,
      reason: finalReason
    });
  };

  const proceedShipment = async () => {
    if (!selectedShipmentForProceed) return;

    try {
      // Prepare the update data with box tracking numbers and unit statuses
      const updateData = {
        status: 'in_transit_to_usa', // Move to destination warehouse
        boxes: selectedShipmentForProceed.boxes.map(box => ({
          id: box.id,
          carrier_tracking_number: box.carrier_tracking_number,
          carrier: box.carrier,
          status: 'in_transit_to_usa',
          units: box.units?.map(unit => ({
            id: unit.id,
            status: 'in_transit'
          })) || []
        }))
      };

      console.log('DEBUG: Sending update data:', updateData);
      console.log('DEBUG: Selected shipment:', selectedShipmentForProceed);

      const response = await authAPI.updateShipmentTracking(selectedShipmentForProceed.id, updateData);
      
      toast.success('Shipment moved to Destination Warehouse! Tracking numbers assigned.');
      queryClient.invalidateQueries(['admin-shipments', activeTab]);
      setShowProceedModal(false);
      setSelectedShipmentForProceed(null);
    } catch (error) {
      console.error('Error proceeding shipment:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || 'Failed to proceed shipment';
      toast.error(errorMessage);
    }
  };

  // Selection handlers
  const toggleShipmentSelection = (shipmentId) => {
    setSelectedShipments(prev => 
      prev.includes(shipmentId) 
        ? prev.filter(id => id !== shipmentId)
        : [...prev, shipmentId]
    );
  };

  const selectAllShipments = () => {
    setSelectedShipments(filteredShipments.map(s => s.id));
  };

  const clearSelection = () => {
    setSelectedShipments([]);
  };

  // Shipment expansion handlers
  const toggleShipmentExpansion = (shipmentId) => {
    setExpandedShipments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(shipmentId)) {
        newSet.delete(shipmentId);
      } else {
        newSet.add(shipmentId);
      }
      return newSet;
    });
  };

  // Child shipment expansion handlers
  const toggleChildShipmentExpansion = (childId) => {
    setExpandedChildShipments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(childId)) {
        newSet.delete(childId);
      } else {
        newSet.add(childId);
      }
      return newSet;
    });
  };

  const getStatusColor = (status, isCancelled = false) => {
    if (isCancelled) {
      return 'status-exception';
    }
    
    switch (status) {
      // Phase 1: Customer Upload (India)
      case 'uploaded':
        return 'status-pending';
      case 'customer_packaging':
        return 'status-processing';
      case 'boxes_created':
        return 'status-processing';
      case 'shipped_to_india_warehouse':
        return 'status-in-transit';
      
      // Phase 2: India Warehouse Processing
      case 'received_in_india':
        return 'status-processing';
      case 'scanned_in_india':
        return 'status-processing';
      case 'international_labels_created':
        return 'status-processing';
      case 'shipped_to_usa':
        return 'status-in-transit';
      case 'in_transit_to_usa':
        return 'status-in-transit';
      
      // Phase 3: USA Warehouse Processing
      case 'received_in_usa':
        return 'status-processing';
      case 'scanned_in_usa':
        return 'status-processing';
      case 'last_mile_labels_created':
        return 'status-processing';
      case 'handed_to_carrier':
        return 'status-processing';
      case 'last_mile_in_transit':
        return 'status-in-transit';
      case 'delivered':
        return 'status-delivered';
      case 'exception':
        return 'status-exception';
      default:
        return 'status-pending';
    }
  };

  // Helper function to check if all non-cancelled child shipments have tracking numbers
  const allNonCancelledUnitsHaveTracking = (units) => {
    if (!units || units.length === 0) return true;
    const nonCancelledUnits = units.filter(unit => !unit.is_cancelled && unit.status !== 'exception');
    return nonCancelledUnits.length === 0 || nonCancelledUnits.every(unit => unit.carrier_tracking_number);
  };

  // Filter shipments based on active tab
  const getFilteredShipments = () => {
    if (!shipments) return [];
    
    console.log('FILTER DEBUG: All shipments:', shipments);
    console.log('FILTER DEBUG: Active tab:', activeTab);
    
    let tabFiltered = [];
    switch (activeTab) {
      case 'waiting-labels':
        tabFiltered = shipments.filter(s => s.status === 'customer_packaging');
        break;
      case 'ready-package':
        tabFiltered = shipments.filter(s => s.status === 'boxes_created');
        break;
      case 'origin-warehouse':
        tabFiltered = shipments.filter(s => 
          ['packaged', 'ready_for_origin_warehouse', 'received_in_india', 'partially_received_india', 'scanned_in_india', 'international_labels_created', 'shipped_to_usa'].includes(s.status)
        );
        break;
      case 'destination-warehouse':
        tabFiltered = shipments.filter(s => 
          ['in_transit_to_usa', 'ready_for_destination_warehouse', 'received_in_usa', 'partially_received_usa', 'scanned_in_usa', 'last_mile_labels_created', 'handed_to_carrier'].includes(s.status)
        );
        break;
      case 'delivered':
        tabFiltered = shipments.filter(s => ['last_mile_in_transit', 'delivered'].includes(s.status));
        break;
      default:
        tabFiltered = shipments;
    }
    
    console.log('FILTER DEBUG: Filtered shipments for', activeTab, ':', tabFiltered);
    
    return tabFiltered.filter(shipment => {
      const matchesSearch = shipment.master_tracking_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shipment.user?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shipment.user?.username?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || shipment.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  };

  const filteredShipments = getFilteredShipments();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Process Shipments</h1>
          <p className="mt-1 text-sm text-gray-500">
            Add tracking numbers and labels for customer shipments
          </p>
          <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded-md">
            <p className="font-medium">Process Flow:</p>
            <ol className="list-decimal list-inside space-y-1 mt-1">
              <li>Export CSV → Process with carrier → Get tracking numbers</li>
              <li>Upload tracking numbers CSV → Assign to child shipments</li>
              <li>Upload PDF labels → Customers can download labels</li>
            </ol>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <FiDownload className="h-4 w-4 mr-2" />
            Export CSV
          </button>
          <button
            onClick={() => setShowTrackingUploadModal(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <FiUpload className="h-4 w-4 mr-2" />
            Upload Tracking
          </button>
          {/* Only show Upload Labels button for waiting-labels and ready-package tabs */}
          {(activeTab === 'waiting-labels' || activeTab === 'ready-package') && (
          <button
            onClick={() => setShowLabelUploadModal(true)}
            disabled={!filteredShipments.some(shipment => 
              allNonCancelledUnitsHaveTracking(shipment.units) && !shipment.has_label
            )}
            className={`flex items-center px-4 py-2 text-sm font-medium border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
              filteredShipments.some(shipment => 
                allNonCancelledUnitsHaveTracking(shipment.units) && !shipment.has_label
              )
                ? 'text-white bg-green-600 hover:bg-green-700'
                : 'text-gray-400 bg-gray-300 cursor-not-allowed'
            }`}
          >
            <FiFileText className="h-4 w-4 mr-2" />
            Upload Labels
          </button>
          )}
        </div>
      </div>

      {/* Workflow Tabs */}
      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {[
              { 
                id: 'waiting-labels', 
                name: 'Waiting for Labels', 
                count: allShipments?.filter(s => s.status === 'customer_packaging').length || 0,
                description: 'Customer requested labels - upload labels here'
              },
              { 
                id: 'ready-package', 
                name: 'Ready to Package', 
                count: allShipments?.filter(s => s.status === 'boxes_created').length || 0,
                description: 'Labels uploaded - customer can package'
              },
              { 
                id: 'origin-warehouse', 
                name: 'Origin Warehouse', 
                count: allShipments?.filter(s => 
                  ['ready_for_origin_warehouse', 'received_in_india', 'partially_received_india', 'scanned_in_india', 'international_labels_created', 'shipped_to_usa'].includes(s.status)
                ).length || 0,
                description: 'India warehouse - receive and process shipments'
              },
              { 
                id: 'destination-warehouse', 
                name: 'Destination Warehouse', 
                count: (() => {
                  const count = allShipments?.filter(s => 
                    ['in_transit_to_usa', 'ready_for_destination_warehouse', 'received_in_usa', 'partially_received_usa', 'scanned_in_usa', 'last_mile_labels_created', 'handed_to_carrier'].includes(s.status)
                  ).length || 0;
                  console.log('TAB COUNT DEBUG: Destination warehouse count:', count);
                  return count;
                })(),
                description: 'USA warehouse - receive and dispatch shipments'
              },
              { 
                id: 'delivered', 
                name: 'Delivered', 
                count: (() => {
                  const count = allShipments?.filter(s => ['last_mile_in_transit', 'delivered'].includes(s.status)).length || 0;
                  console.log('TAB COUNT DEBUG: Delivered count:', count);
                  return count;
                })(),
                description: 'Shipments ready for delivery or delivered'
              }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                {tab.name}
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                  activeTab === tab.id
                    ? 'bg-red-100 text-red-600'
                    : 'bg-gray-100 text-gray-900'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>
        <div className="px-6 py-3 bg-gray-50">
          <p className="text-sm text-gray-600">
            {activeTab === 'waiting-labels' && 'Customer requested labels - upload labels here'}
            {activeTab === 'ready-package' && 'Labels uploaded - customer can package'}
            {activeTab === 'origin-warehouse' && 'India warehouse - receive and process shipments'}
            {activeTab === 'destination-warehouse' && 'USA warehouse - receive and dispatch shipments'}
            {activeTab === 'delivered' && 'Shipments ready for delivery or delivered'}
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search shipments by tracking number, company, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
          
          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="all">All Statuses</option>
              <option value="uploaded">Uploaded (New Orders)</option>
              <option value="customer_packaging">Waiting for Labels</option>
              <option value="boxes_created">Ready to Package</option>
              <option value="shipped_to_india_warehouse">Shipped to India</option>
              <option value="received_in_india">Received in India</option>
              <option value="scanned_in_india">Scanned in India</option>
              <option value="international_labels_created">International Labels Created</option>
              <option value="shipped_to_usa">Shipped to USA</option>
              <option value="in_transit_to_usa">In Transit to USA</option>
              <option value="received_in_usa">Received in USA</option>
              <option value="scanned_in_usa">Scanned in USA</option>
              <option value="last_mile_labels_created">Last Mile Labels Created</option>
              <option value="handed_to_carrier">Handed to Carrier</option>
              <option value="last_mile_in_transit">Last Mile In Transit</option>
              <option value="delivered">Delivered</option>
              <option value="exception">Exception</option>
            </select>
          </div>
        </div>
      </div>

      {/* Selection Controls */}
      {filteredShipments.length > 0 && (
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={selectAllShipments}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Select All ({filteredShipments.length})
              </button>
              <button
                onClick={clearSelection}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Clear Selection
              </button>
              {selectedShipments.length > 0 && (
                <span className="text-sm text-gray-500">
                  {selectedShipments.length} selected
                </span>
              )}
            </div>
            {selectedShipments.length > 0 && (
              <button
                onClick={exportShipmentsCSV}
                className="flex items-center px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
              >
                <FiDownload className="h-4 w-4 mr-1" />
                Export Selected
              </button>
            )}
          </div>
        </div>
      )}

      {/* Shipments List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {activeTab === 'waiting-labels' && 'Waiting for Labels'}
                {activeTab === 'ready-package' && 'Ready to Package'}
                {activeTab === 'origin-warehouse' && 'Origin Warehouse (India)'}
                {activeTab === 'destination-warehouse' && 'Destination Warehouse (USA)'}
                {activeTab === 'delivered' && 'Delivery Management'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {filteredShipments.length} shipments
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {activeTab === 'waiting-labels' && (
                <div className="flex items-center space-x-2">
                  <span className="px-3 py-1 text-sm bg-yellow-100 text-yellow-800 rounded-full">
                    Upload Labels Required
                  </span>
                  <div className="text-xs text-gray-500">
                    Step 1: Export CSV → Step 2: Upload tracking numbers → Step 3: Upload labels
                  </div>
                </div>
              )}
              {activeTab === 'ready-package' && (
                <span className="px-3 py-1 text-sm bg-green-100 text-green-800 rounded-full">
                  Ready for Customer
                </span>
              )}
              {activeTab === 'origin-warehouse' && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setWarehouseType('origin');
                      setShowBoxScanModal(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <FiPackage className="h-4 w-4 mr-2 inline" />
                    Receive Box
                  </button>
                  <button
                    onClick={() => setShowBoxTrackingModal(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    <FiUpload className="h-4 w-4 mr-2 inline" />
                    Upload Box Tracking
                  </button>
                </div>
              )}
              {activeTab === 'destination-warehouse' && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setWarehouseType('destination');
                      setShowBoxScanModal(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <FiPackage className="h-4 w-4 mr-2 inline" />
                    Receive by Tracking Number
                  </button>
                </div>
              )}
              {activeTab === 'delivered' && (
                <div className="flex items-center space-x-2">
                  <span className="px-3 py-1 text-sm bg-green-100 text-green-800 rounded-full">
                    All Delivered
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {filteredShipments.length > 0 ? (
            <div className="space-y-4">
              {filteredShipments.map((shipment) => {
          const isShipmentExpanded = expandedShipments.has(shipment.id);
          return (
            <div key={shipment.id} className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedShipments.includes(shipment.id)}
                      onChange={() => toggleShipmentSelection(shipment.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                    />
                    <button
                      onClick={() => toggleShipmentExpansion(shipment.id)}
                      className="flex items-center hover:bg-gray-50 p-1 rounded transition-colors"
                    >
                      {isShipmentExpanded ? (
                        <FiChevronDown className="h-5 w-5 text-gray-400 mr-2" />
                      ) : (
                        <FiChevronRight className="h-5 w-5 text-gray-400 mr-2" />
                      )}
                      <FiTruck className="h-6 w-6 text-gray-400 mr-3" />
                      <div className="text-left">
                        <h3 className="text-lg font-medium text-gray-900">
                          {shipment.master_tracking_number || 'No ID'}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {shipment.user?.company_name} ({shipment.user?.username})
                        </p>
                        <div className="text-xs text-gray-500 mt-1">
                          {shipment.units?.length || 0} units
                          {(() => {
                            const nonCancelledUnits = shipment.units?.filter(unit => !unit.is_cancelled && unit.status !== 'exception') || [];
                            const hasSomeTracking = nonCancelledUnits.some(unit => unit.carrier_tracking_number);
                            const hasAllTracking = allNonCancelledUnitsHaveTracking(shipment.units);
                            
                            if (hasSomeTracking && !hasAllTracking) {
                              return (
                                <span className="ml-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
                                  Partial Tracking
                                </span>
                              );
                            } else if (!hasSomeTracking && nonCancelledUnits.length > 0) {
                              return (
                                <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                                  No Tracking Numbers
                                </span>
                              );
                            } else if (hasAllTracking && nonCancelledUnits.length > 0) {
                              return (
                                <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                                  Ready for Labels
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {shipment.label_file && (
                            <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded flex items-center">
                              <FiFileText className="h-3 w-3 mr-1" />
                              Master Label
                            </span>
                          )}
                          {shipment.boxes?.some(box => box.label_file) && (
                            <span className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded flex items-center">
                              <FiPackage className="h-3 w-3 mr-1" />
                              Box Labels
                            </span>
                          )}
                          {shipment.boxes?.some(box => 
                            box.units?.some(unit => unit.label_file)
                          ) && (
                            <span className="px-2 py-1 text-xs bg-pink-100 text-pink-800 rounded flex items-center">
                              <FiFileText className="h-3 w-3 mr-1" />
                              Unit Labels
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`status-badge ${getStatusColor(shipment.status)}`}>
                      {shipment.status.replace('_', ' ')}
                    </span>
                    {/* Only show label upload/download for waiting-labels and ready-package tabs */}
                    {(activeTab === 'waiting-labels' || activeTab === 'ready-package') && (
                      <>
                    {shipment.has_label ? (
                      <div className="flex items-center space-x-1">
                        <FiCheckCircle className="h-4 w-4 text-green-500" />
                        <a
                          href={shipment.label_file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-green-600 hover:text-green-800"
                        >
                          Download Label
                        </a>
                      </div>
                    ) : (
                      // Check if all non-cancelled units have tracking numbers before allowing label upload
                      allNonCancelledUnitsHaveTracking(shipment.units) ? (
                        <button
                          onClick={() => {
                            setSelectedMasterShipment(shipment);
                            setShowLabelUploadModal(true);
                          }}
                          className="flex items-center px-3 py-1 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100"
                        >
                          <FiFileText className="h-4 w-4 mr-1" />
                          Upload Label
                        </button>
                      ) : (
                        <div className="flex items-center space-x-1 text-gray-500">
                          <FiAlertCircle className="h-4 w-4" />
                          <span className="text-sm">Upload tracking numbers first</span>
                        </div>
                      )
                    )}
                      </>
                    )}
                    {/* Action buttons based on tab */}
                    {activeTab === 'origin-warehouse' && (
                      <>
                        {/* Only show Proceed button if ALL boxes have tracking numbers */}
                        {shipment.boxes && shipment.boxes.length > 0 && 
                         shipment.boxes.every(box => box.carrier_tracking_number) ? (
                      <button
                        onClick={() => handleProceed(shipment)}
                        className="flex items-center px-3 py-1 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                      >
                        <FiCheckCircle className="h-4 w-4 mr-1" />
                            Proceed to Destination
                      </button>
                        ) : (
                          <div className="flex items-center space-x-1 text-gray-500">
                            <FiAlertCircle className="h-4 w-4" />
                            <span className="text-sm">
                              {shipment.boxes && shipment.boxes.length > 0 
                                ? 'Upload tracking numbers for all boxes first'
                                : 'No boxes available'
                              }
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    {activeTab === 'destination-warehouse' && (
                      <>
                        {/* Only show Receive button if shipment hasn't been received yet */}
                        {!['received_in_usa', 'partially_received_usa', 'scanned_in_usa', 'last_mile_labels_created', 'handed_to_carrier', 'last_mile_in_transit', 'delivered'].includes(shipment.status) && (
                          <button
                            onClick={() => handleReceive(shipment)}
                            className="flex items-center px-3 py-1 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                          >
                            <FiPackage className="h-4 w-4 mr-1" />
                            Receive
                          </button>
                        )}
                        {/* Only show Dispatch button if shipment has been received */}
                        {['received_in_usa', 'partially_received_usa'].includes(shipment.status) && (
                          <button
                            onClick={() => handleDispatch(shipment)}
                            className="flex items-center px-3 py-1 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700"
                          >
                            <FiTruck className="h-4 w-4 mr-1" />
                            Dispatch
                          </button>
                        )}
                        {/* Show status indicator if already processed */}
                        {['scanned_in_usa', 'last_mile_labels_created', 'handed_to_carrier', 'last_mile_in_transit', 'delivered'].includes(shipment.status) && (
                          <div className="flex items-center space-x-1 text-gray-500">
                            <FiCheckCircle className="h-4 w-4" />
                            <span className="text-sm">
                              {shipment.status === 'scanned_in_usa' && 'Scanned'}
                              {shipment.status === 'last_mile_labels_created' && 'Labels Created'}
                              {shipment.status === 'handed_to_carrier' && 'Handed to Carrier'}
                              {shipment.status === 'last_mile_in_transit' && 'In Transit'}
                              {shipment.status === 'delivered' && 'Delivered'}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    {activeTab === 'delivered' && shipment.status !== 'delivered' && (
                      <button
                        onClick={() => handleDeliver(shipment)}
                        className="flex items-center px-3 py-1 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                      >
                        <FiCheckCircle className="h-4 w-4 mr-1" />
                        Mark Delivered
                      </button>
                    )}
                    {activeTab === 'delivered' && shipment.status === 'delivered' && (
                      <div className="flex items-center space-x-1">
                        <FiCheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-green-600 font-medium">Delivered</span>
                      </div>
                    )}
                    <button
                      onClick={() => handleEdit(shipment)}
                      className="flex items-center px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      <FiEdit className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                  </div>
                </div>
              </div>

              {/* Expandable Shipment Details */}
              {isShipmentExpanded && (
                <div className="px-6 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Origin</h4>
                      <p className="text-sm text-gray-600">{shipment.origin_address}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Destination</h4>
                      <p className="text-sm text-gray-600">{shipment.destination_address}</p>
                    </div>
                  </div>

                  {/* Conditional Rendering Based on Tab - Following Customer Side Pattern */}
                  {activeTab === 'origin-warehouse' || activeTab === 'destination-warehouse' ? (
                    // Show Master -> Box -> Child structure for warehouse tabs
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Packaging Structure
                    </h4>
                      
                      {/* Tracking Status Summary */}
                      {shipment.boxes && shipment.boxes.length > 0 && (
                        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center">
                                <FiPackage className="h-4 w-4 text-gray-500 mr-2" />
                                <span className="text-sm text-gray-600">
                                  Total Boxes: {shipment.boxes.length}
                                </span>
                              </div>
                              <div className="flex items-center">
                                <FiCheckCircle className="h-4 w-4 text-green-500 mr-2" />
                                <span className="text-sm text-green-600">
                                  With Tracking: {shipment.boxes.filter(box => box.carrier_tracking_number).length}
                                </span>
                              </div>
                              <div className="flex items-center">
                                <FiAlertCircle className="h-4 w-4 text-red-500 mr-2" />
                                <span className="text-sm text-red-600">
                                  Missing Tracking: {shipment.boxes.filter(box => !box.carrier_tracking_number).length}
                                </span>
                              </div>
                            </div>
                            <div className="text-sm">
                              {shipment.boxes.every(box => box.carrier_tracking_number) ? (
                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                                  Ready to Proceed
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                                  Tracking Required
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="space-y-4">
                        {shipment.boxes && shipment.boxes.length > 0 ? (
                          shipment.boxes.map((box, boxIndex) => {
                            const isBoxExpanded = expandedChildShipments.has(`box-${box.id}`);
                        return (
                              <div key={box.id} className="bg-white p-4 rounded border border-gray-200">
                            <div 
                                  className="flex items-center justify-between mb-3 cursor-pointer hover:bg-gray-50 transition-colors"
                                  onClick={() => toggleChildShipmentExpansion(`box-${box.id}`)}
                            >
                                <div className="flex items-center">
                                    {isBoxExpanded ? (
                                    <FiChevronDown className="h-4 w-4 text-gray-400 mr-2" />
                                  ) : (
                                    <FiChevronRight className="h-4 w-4 text-gray-400 mr-2" />
                                  )}
                                    <FiPackage className="h-5 w-5 text-blue-500 mr-2" />
                                  <div>
                                      <h5 className="font-medium text-gray-900">{box.box_name || `Box ${boxIndex + 1}`}</h5>
                                      <p className="text-sm text-gray-500">Box ID: {box.box_tracking_number}</p>
                                      {box.carrier_tracking_number ? (
                                        <p className="text-xs text-blue-600 font-medium">
                                          <FiCheckCircle className="h-3 w-3 inline mr-1" />
                                          Tracking: {box.carrier_tracking_number}
                                        </p>
                                      ) : (
                                        <p className="text-xs text-red-600 font-medium">
                                          <FiAlertCircle className="h-3 w-3 inline mr-1" />
                                          Tracking number required
                                        </p>
                                      )}
                                    </div>
                                    </div>
                                  <div className="text-right">
                                    <span className={`px-2 py-1 text-xs rounded-full ${
                                      box.status === 'received_in_india' || box.status === 'received_in_usa' 
                                        ? 'bg-green-100 text-green-800'
                                        : box.status === 'partially_received_india' || box.status === 'partially_received_usa'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {box.status.replace('_', ' ')}
                                    </span>
                                      </div>
                                      </div>
                                
                                {isBoxExpanded && box.units && box.units.length > 0 && (
                                  <div className="ml-6">
                                    <h6 className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
                                      Units in this box ({box.units.length})
                                    </h6>
                                    <div className="space-y-2">
                                      {box.units.map((unit, unitIndex) => {
                                        const isUnitExpanded = expandedChildShipments.has(`unit-${unit.id}`);
                                        return (
                                          <div key={unit.id} className="bg-gray-50 p-3 rounded border border-gray-100">
                                            <div 
                                              className="cursor-pointer hover:bg-gray-100 transition-colors"
                                              onClick={() => toggleChildShipmentExpansion(`unit-${unit.id}`)}
                                            >
                                              <div className="flex items-center justify-between">
                                                <div className="flex items-center">
                                                  {isUnitExpanded ? (
                                                    <FiChevronDown className="h-3 w-3 text-gray-400 mr-2" />
                                                  ) : (
                                                    <FiChevronRight className="h-3 w-3 text-gray-400 mr-2" />
                                                  )}
                                                  <FiPackage className="h-3 w-3 text-gray-400 mr-2" />
                                                  <div>
                                                    <div className="text-sm font-medium text-gray-900">{unit.customer_name}</div>
                                                    <div className="text-xs text-gray-500">{unit.item_description?.substring(0, 40)}...</div>
                                  </div>
                                </div>
                                                <div className="flex items-center space-x-1">
                                                  <span className={`px-1 py-0.5 text-xs rounded-full ${
                                                    unit.status === 'delivered' ? 'bg-green-100 text-green-800' :
                                                    unit.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                                                    'bg-gray-100 text-gray-800'
                                                  }`}>
                                    {unit.status.replace('_', ' ')}
                                  </span>
                                                  {unit.carrier_tracking_number && (
                                                    <span className="px-1 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                                                      {unit.carrier_tracking_number}
                                    </span>
                                  )}
                                                  {/* Individual Mark Delivered button for delivered tab */}
                                                  {activeTab === 'delivered' && unit.status !== 'delivered' && (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeliverChildShipment(shipment, unit);
                                                      }}
                                                      className="ml-2 px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                                                      disabled={deliverChildShipmentMutation.isLoading}
                                                    >
                                                      <FiCheckCircle className="h-3 w-3 inline mr-1" />
                                                      Mark Delivered
                                                    </button>
                                                  )}
                                                  {activeTab === 'delivered' && unit.status === 'delivered' && (
                                                    <div className="ml-2 flex items-center text-green-600">
                                                      <FiCheckCircle className="h-3 w-3 mr-1" />
                                                      <span className="text-xs font-medium">Delivered</span>
                                                    </div>
                                                  )}
                                </div>
                              </div>
                            </div>
                            
                                            {/* Unit Details - Expanded */}
                            {isUnitExpanded && (
                                              <div className="mt-3 pt-3 border-t border-gray-200">
                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                                                  <div className="flex items-start">
                                                    <FiUser className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                                  <div>
                                                      <div className="font-medium text-gray-900">{unit.customer_name}</div>
                                                      <div className="text-gray-500">{unit.customer_email}</div>
                                                      <div className="text-gray-500">{unit.customer_phone}</div>
                                                      {unit.carrier_tracking_number && (
                                                        <div className="text-xs text-blue-600 font-medium flex items-center mt-1">
                                                          <FiCheckCircle className="h-3 w-3 mr-1" />
                                                          Tracking: {unit.carrier_tracking_number}
                                  </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                  <div className="flex items-start">
                                                    <FiMapPin className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                                  <div>
                                                      <div className="text-gray-900">{unit.address_line1}</div>
                                                      {unit.address_line2 && <div className="text-gray-600">{unit.address_line2}</div>}
                                                      <div className="text-gray-600">{unit.city}, {unit.state} {unit.zip}</div>
                                                      <div className="text-gray-500">{unit.country}</div>
                                  </div>
                                                  </div>
                                                  <div className="flex items-start">
                                                    <FiBox className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                                  <div>
                                                      <div className="font-medium text-gray-900">{unit.item_description}</div>
                                                      <div className="text-gray-500">HSN: {unit.hsn_code}</div>
                                                      <div className="text-gray-500">Value: ${unit.item_value}</div>
                                                      <div className="text-gray-500">Ref: {unit.reference_id}</div>
                                  </div>
                                                  </div>
                                                  <div className="flex items-start justify-between">
                                  <div>
                                                      <div className="text-gray-600 font-medium">
                                                        {unit.weight_gms}g
                                                      </div>
                                                      <div className="text-gray-500">
                                                        {unit.length_cm}×{unit.width_cm}×{unit.height_cm}cm
                                                      </div>
                                                      {unit.label_file_url && (
                                                        <div className="mt-2">
                                                          <span className="px-2 py-1 text-xs bg-pink-100 text-pink-800 rounded flex items-center">
                                                            <FiFileText className="h-3 w-3 mr-1" />
                                                            Unit Label
                                                          </span>
                                                        </div>
                                                      )}
                                                    </div>
                                                    {unit.label_file_url && (
                                                      <div className="flex items-center space-x-1">
                                                        <button 
                                                          onClick={() => window.open(unit.label_file_url, '_blank')}
                                                          className="text-blue-600 hover:text-blue-900 text-xs"
                                                          title="View Unit Label"
                                                        >
                                                          <FiEye className="h-4 w-4" />
                                                        </button>
                                                        <button 
                                                          onClick={() => {
                                                            const link = document.createElement('a');
                                                            link.href = unit.label_file_url;
                                                            link.download = `Unit_${unit.child_tracking_number || unit.id}.pdf`;
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                          }}
                                                          className="text-green-600 hover:text-green-900 text-xs"
                                                          title="Download Unit Label"
                                                        >
                                                          <FiDownload className="h-4 w-4" />
                                                        </button>
                                                      </div>
                                                    )}
                                      </div>
                                    </div>
                                  </div>
                                            )}
                                  </div>
                                        );
                                      })}
                                  </div>
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center py-4 text-gray-500">
                            <FiPackage className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                            <p>No boxes created yet</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Show Master -> Child structure for other tabs
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Order Items ({shipment.units?.length || 0} units)
                      </h4>
                      <div className="space-y-3">
                        {shipment.units?.map((unit, unitIndex) => {
                          const isUnitExpanded = expandedChildShipments.has(`unit-${unit.id}`);
                          return (
                            <div key={unit.id} className="bg-white p-4 rounded border border-gray-200">
                              <div 
                                className="cursor-pointer hover:bg-gray-50 transition-colors"
                                onClick={() => toggleChildShipmentExpansion(`unit-${unit.id}`)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    {isUnitExpanded ? (
                                      <FiChevronDown className="h-4 w-4 text-gray-400 mr-2" />
                                    ) : (
                                      <FiChevronRight className="h-4 w-4 text-gray-400 mr-2" />
                                    )}
                                    <FiPackage className="h-4 w-4 text-gray-400 mr-2" />
                                  <div>
                                      <div className="text-sm font-medium text-gray-900">
                                        {unit.customer_name || 'Unnamed Customer'}
                                  </div>
                                      <div className="text-xs text-gray-500">
                                        {unit.item_description?.substring(0, 50)}...
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <span className={`px-2 py-1 text-xs rounded ${getStatusColor(unit.status, unit.is_cancelled)}`}>
                                      {unit.is_cancelled ? 'Cancelled' : unit.status.replace('_', ' ')}
                                    </span>
                                    {unit.carrier_tracking_number && (
                                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                        {unit.carrier_tracking_number}
                                      </span>
                                    )}
                                    {/* Individual Mark Delivered button for delivered tab */}
                                    {activeTab === 'delivered' && unit.status !== 'delivered' && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeliverChildShipment(shipment, unit);
                                        }}
                                        className="ml-2 px-2 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                                        disabled={deliverChildShipmentMutation.isLoading}
                                      >
                                        <FiCheckCircle className="h-3 w-3 inline mr-1" />
                                        Mark Delivered
                                      </button>
                                    )}
                                    {activeTab === 'delivered' && unit.status === 'delivered' && (
                                      <div className="ml-2 flex items-center text-green-600">
                                        <FiCheckCircle className="h-3 w-3 mr-1" />
                                        <span className="text-xs font-medium">Delivered</span>
                                      </div>
                                    )}
                                    {/* Cancel button for waiting-labels tab - only show if no tracking number and not already cancelled */}
                                    {activeTab === 'waiting-labels' && !unit.carrier_tracking_number && !unit.is_cancelled && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCancelChildShipment(unit.id);
                                        }}
                                        className="ml-2 px-2 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
                                        disabled={cancelChildShipmentMutation.isLoading}
                                      >
                                        <FiXCircle className="h-3 w-3 inline mr-1" />
                                        Cancel
                                      </button>
                                    )}
                                    {/* Show cancelled status if already cancelled */}
                                    {activeTab === 'waiting-labels' && unit.is_cancelled && (
                                      <div className="ml-2 flex items-center text-red-600">
                                        <FiXCircle className="h-3 w-3 mr-1" />
                                        <span className="text-xs font-medium">Cancelled</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Unit Details - Expanded */}
                              {isUnitExpanded && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                                    <div className="flex items-start">
                                      <FiUser className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                                  <div>
                                        <div className="font-medium text-gray-900">{unit.customer_name}</div>
                                        <div className="text-gray-500">{unit.customer_email}</div>
                                        <div className="text-gray-500">{unit.customer_phone}</div>
                                        {unit.carrier_tracking_number && (
                                          <div className="text-xs text-blue-600 font-medium flex items-center mt-1">
                                            <FiCheckCircle className="h-3 w-3 mr-1" />
                                            Tracking: {unit.carrier_tracking_number}
                                  </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-start">
                                      <FiMapPin className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                                  <div>
                                        <div className="text-gray-900">{unit.address_line1}</div>
                                        {unit.address_line2 && <div className="text-gray-600">{unit.address_line2}</div>}
                                        <div className="text-gray-600">{unit.city}, {unit.state} {unit.zip}</div>
                                        <div className="text-gray-500">{unit.country}</div>
                                    </div>
                                  </div>
                                    <div className="flex items-start">
                                      <FiBox className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                                  <div>
                                        <div className="font-medium text-gray-900">{unit.item_description}</div>
                                        <div className="text-gray-500">HSN: {unit.hsn_code}</div>
                                        <div className="text-gray-500">Value: ${unit.item_value}</div>
                                        <div className="text-gray-500">Ref: {unit.reference_id}</div>
                                  </div>
                                    </div>
                                    <div className="flex items-start justify-between">
                                  <div>
                                        <div className="text-gray-600 font-medium">
                                          {unit.weight_gms}g
                                        </div>
                                        <div className="text-gray-500">
                                          {unit.length_cm}×{unit.width_cm}×{unit.height_cm}cm
                                        </div>
                                        <div className="mt-2">
                                          <span className={`px-2 py-1 text-xs rounded-full ${
                                            unit.status === 'delivered' ? 'bg-green-100 text-green-800' :
                                            unit.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                                            'bg-gray-100 text-gray-800'
                                          }`}>
                                            {unit.status.replace('_', ' ')}
                                        </span>
                                        </div>
                                        {unit.label_file_url && (
                                          <div className="mt-2">
                                            <span className="px-2 py-1 text-xs bg-pink-100 text-pink-800 rounded flex items-center">
                                              <FiFileText className="h-3 w-3 mr-1" />
                                              Unit Label
                                        </span>
                                          </div>
                                      )}
                                    </div>
                                      {unit.label_file_url && (
                                        <div className="flex items-center space-x-1">
                                          <button 
                                            onClick={() => window.open(unit.label_file_url, '_blank')}
                                            className="text-blue-600 hover:text-blue-900 text-xs"
                                            title="View Unit Label"
                                          >
                                            <FiEye className="h-4 w-4" />
                                          </button>
                                          <button 
                                            onClick={() => {
                                              const link = document.createElement('a');
                                              link.href = unit.label_file_url;
                                              link.download = `Unit_${unit.child_tracking_number || unit.id}.pdf`;
                                              document.body.appendChild(link);
                                              link.click();
                                              document.body.removeChild(link);
                                            }}
                                            className="text-green-600 hover:text-green-900 text-xs"
                                            title="Download Unit Label"
                                          >
                                            <FiDownload className="h-4 w-4" />
                                          </button>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
            </div>
          ) : (
            <div className="text-center py-12">
              <FiTruck className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {activeTab === 'waiting-labels' && 'No shipments waiting for labels'}
                {activeTab === 'ready-package' && 'No shipments ready for packaging'}
                {activeTab === 'origin-warehouse' && 'No shipments in origin warehouse'}
                {activeTab === 'destination-warehouse' && 'No shipments in destination warehouse'}
                {activeTab === 'delivered' && 'No shipments ready for delivery'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {activeTab === 'waiting-labels' && 'All shipments have labels uploaded or are in other stages.'}
                {activeTab === 'ready-package' && 'No shipments are ready for customer packaging yet.'}
                {activeTab === 'origin-warehouse' && 'No shipments are currently in the origin warehouse.'}
                {activeTab === 'destination-warehouse' && 'No shipments are currently in the destination warehouse.'}
                {activeTab === 'delivered' && 'No shipments are ready for delivery or have been delivered yet.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingShipment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Edit Shipment Details
                </h3>
                <button
                  onClick={() => setEditingShipment(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Master Shipment Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Master ID (Auto-generated)
                    </label>
                    <input
                      type="text"
                      value={editingShipment.master_tracking_number || ''}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={editingShipment.status}
                      onChange={(e) => setEditingShipment(prev => ({
                        ...prev,
                        status: e.target.value
                      }))}
                      className="form-select"
                    >
                      <option value="uploaded">Uploaded by Customer</option>
                      <option value="customer_packaging">Customer Packaging</option>
                      <option value="boxes_created">Boxes Created</option>
                      <option value="shipped_to_india_warehouse">Shipped to India Warehouse</option>
                      <option value="received_in_india">Received in India Warehouse</option>
                      <option value="scanned_in_india">Scanned in India Warehouse</option>
                      <option value="international_labels_created">International Labels Created</option>
                      <option value="shipped_to_usa">Shipped to USA Warehouse</option>
                      <option value="in_transit_to_usa">In Transit to USA</option>
                      <option value="received_in_usa">Received in USA Warehouse</option>
                      <option value="scanned_in_usa">Scanned in USA Warehouse</option>
                      <option value="last_mile_labels_created">Last Mile Labels Created</option>
                      <option value="handed_to_carrier">Handed to Last Mile Carrier</option>
                      <option value="last_mile_in_transit">Last Mile In Transit</option>
                      <option value="delivered">Delivered</option>
                      <option value="exception">Exception</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Admin Notes
                  </label>
                  <textarea
                    value={editingShipment.admin_notes || ''}
                    onChange={(e) => setEditingShipment(prev => ({
                      ...prev,
                      admin_notes: e.target.value
                    }))}
                    className="form-textarea"
                    rows={3}
                    placeholder="Add any notes about this shipment..."
                  />
                </div>

                {/* Boxes and Units */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">
                    Boxes & Units
                  </h4>
                  <div className="space-y-4">
                    {editingShipment.boxes?.map((box, boxIndex) => (
                      <div key={box.id} className="p-4 border border-gray-200 rounded-lg">
                        <h5 className="text-md font-medium text-gray-800 mb-3">
                          Box {boxIndex + 1}: {box.box_name || box.box_tracking_number}
                        </h5>
                        
                        {/* Box Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Box ID (Auto-generated)
                            </label>
                            <input
                              type="text"
                              value={box.box_tracking_number || ''}
                              disabled
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Carrier Tracking Number
                            </label>
                            <input
                              type="text"
                              value={box.carrier_tracking_number || ''}
                              onChange={(e) => handleBoxChange(boxIndex, 'carrier_tracking_number', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="e.g., 1Z999AA1234567890"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Carrier
                            </label>
                            <select
                              value={box.carrier || ''}
                              onChange={(e) => handleBoxChange(boxIndex, 'carrier', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select Carrier</option>
                              <option value="UPS">UPS</option>
                              <option value="USPS">USPS</option>
                              <option value="FedEx">FedEx</option>
                              <option value="DHL">DHL</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Status
                            </label>
                            <select
                              value={box.status}
                              onChange={(e) => handleBoxChange(boxIndex, 'status', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="created">Box Created</option>
                              <option value="packed">Box Packed</option>
                              <option value="shipped_to_india_warehouse">Shipped to India Warehouse</option>
                              <option value="received_in_india">Received in India Warehouse</option>
                              <option value="scanned_in_india">Scanned in India Warehouse</option>
                              <option value="international_label_created">International Label Created</option>
                              <option value="shipped_to_usa">Shipped to USA Warehouse</option>
                              <option value="in_transit_to_usa">In Transit to USA</option>
                              <option value="received_in_usa">Received in USA Warehouse</option>
                              <option value="scanned_in_usa">Scanned in USA Warehouse</option>
                              <option value="last_mile_label_created">Last Mile Label Created</option>
                              <option value="handed_to_carrier">Handed to Last Mile Carrier</option>
                              <option value="last_mile_in_transit">Last Mile In Transit</option>
                              <option value="out_for_delivery">Out for Delivery</option>
                              <option value="delivered">Delivered</option>
                              <option value="exception">Exception</option>
                            </select>
                          </div>
                        </div>

                        {/* Units in this Box */}
                        <div className="mt-4">
                          <h6 className="text-sm font-medium text-gray-700 mb-2">
                            Units in this box ({box.units?.length || 0})
                          </h6>
                          <div className="space-y-3">
                            {box.units?.map((unit, unitIndex) => (
                              <div key={unit.id} className="p-3 bg-gray-50 border border-gray-200 rounded">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Unit ID (Auto-generated)
                                    </label>
                                    <input
                                      type="text"
                                      value={unit.child_tracking_number || ''}
                                      disabled
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Carrier Tracking Number
                                    </label>
                                    <input
                                      type="text"
                                      value={unit.carrier_tracking_number || ''}
                                      onChange={(e) => handleUnitChange(boxIndex, unitIndex, 'carrier_tracking_number', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      placeholder="e.g., 1Z999AA1234567890"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Status
                                    </label>
                                    <select
                                      value={unit.status}
                                      onChange={(e) => handleUnitChange(boxIndex, unitIndex, 'status', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="created">Unit Created</option>
                                      <option value="labeled">Unit Labeled</option>
                                      <option value="packed_in_box">Packed in Box</option>
                                      <option value="shipped_to_india_warehouse">Shipped to India Warehouse</option>
                                      <option value="received_in_india">Received in India Warehouse</option>
                                      <option value="scanned_in_india">Scanned in India Warehouse</option>
                                      <option value="shipped_to_usa">Shipped to USA Warehouse</option>
                                      <option value="in_transit_to_usa">In Transit to USA</option>
                                      <option value="received_in_usa">Received in USA Warehouse</option>
                                      <option value="scanned_in_usa">Scanned in USA Warehouse</option>
                                      <option value="last_mile_label_created">Last Mile Label Created</option>
                                      <option value="handed_to_carrier">Handed to Last Mile Carrier</option>
                                      <option value="last_mile_in_transit">Last Mile In Transit</option>
                                      <option value="out_for_delivery">Out for Delivery</option>
                                      <option value="delivered">Delivered</option>
                                      <option value="exception">Exception</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Customer
                                    </label>
                                    <input
                                      type="text"
                                      value={unit.customer_name || ''}
                                      disabled
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                                    />
                                  </div>
                                </div>
                                <div className="mt-2 text-xs text-gray-600">
                                  <strong>Item:</strong> {unit.item_description} | 
                                  <strong> Address:</strong> {unit.address_line1}, {unit.city}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setEditingShipment(null)}
                    className="btn btn-secondary"
                    disabled={updateMutation.isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={updateMutation.isLoading}
                    className="btn btn-primary"
                  >
                    {updateMutation.isLoading ? (
                      <>
                        <div className="spinner mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FiSave className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Export Shipments</h3>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              <div className="text-sm text-gray-500 mb-4">
                <p>Export {filteredShipments.length} master shipments to CSV format.</p>
                <div className="mt-2 p-2 bg-blue-50 rounded-md">
                  <p className="font-medium text-blue-800">What's included:</p>
                  <ul className="text-xs text-blue-700 mt-1 space-y-1">
                    <li>• Each unit (order item) as a separate row</li>
                    <li>• Complete recipient details and item information</li>
                    <li>• Unit IDs for tracking number assignment</li>
                    <li>• Master shipment details for reference</li>
                    <li>• All required fields: address, weight, dimensions, HSN code, etc.</li>
                  </ul>
                </div>
                <p className="mt-2 text-xs text-gray-600">
                  Use the exported CSV to get unit_id values for tracking number upload. The "Unit ID (Child Shipment ID)" column contains the unit IDs you need.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowExportModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    exportShipmentsCSV();
                    setShowExportModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  <FiDownload className="h-4 w-4 mr-2 inline" />
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Upload Modal */}
      {showTrackingUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Upload Tracking Numbers</h3>
                <button
                  onClick={() => setShowTrackingUploadModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CSV File Format
                  </label>
                  <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md">
                    <p className="font-medium mb-2">Required columns:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>child_shipment_id</strong> - Unit ID from exported CSV</li>
                      <li><strong>tracking_number</strong> - Tracking number from carrier</li>
                      <li><strong>carrier</strong> - UPS, USPS, FedEx, etc. (optional)</li>
                    </ul>
                    <div className="mt-3 pt-2 border-t border-gray-200">
                      <p className="font-medium text-blue-600">How to get child_shipment_id:</p>
                      <p className="text-xs">1. Export shipments CSV first</p>
                      <p className="text-xs">2. Use the "Unit ID (Child Shipment ID)" column values (these are unit IDs)</p>
                      <p className="text-xs">3. Each row represents one unit/order item</p>
                    </div>
                  </div>
                  <button
                    onClick={downloadTrackingTemplate}
                    className="mt-2 flex items-center px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                  >
                    <FiDownload className="h-3 w-3 mr-1" />
                    Download Template
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select CSV File
                  </label>
                  <input
                    type="file"
                    accept=".csv,text/csv,application/csv,text/plain"
                    onChange={handleTrackingFileUpload}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {trackingFile && (
                    <p className="text-sm text-green-600 mt-1">
                      <FiCheckCircle className="h-4 w-4 inline mr-1" />
                      {trackingFile.name} selected
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowTrackingUploadModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={uploadTrackingNumbers}
                  disabled={!trackingFile}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiUpload className="h-4 w-4 mr-2 inline" />
                  Upload
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comprehensive Label Upload Modal */}
      {showLabelUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Upload Labels</h3>
                <button
                  onClick={() => {
                    setShowLabelUploadModal(false);
                    setLabelUploadType('master');
                    setSelectedMasterShipment(null);
                    setSelectedUnitForLabel(null);
                    setLabelFile(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Upload Type Selection */}
                <div>
                  <h4 className="text-md font-medium text-gray-800 mb-3">Select Upload Type</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={() => setLabelUploadType('master')}
                      className={`p-4 border-2 rounded-lg text-left transition-colors ${
                        labelUploadType === 'master' 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center mb-2">
                        <FiTruck className="h-5 w-5 text-gray-600 mr-2" />
                        <span className="font-medium">Master Shipment</span>
                      </div>
                      <p className="text-sm text-gray-600">Upload single PDF for entire shipment</p>
                    </button>
                    
                    <button
                      onClick={() => setLabelUploadType('unit')}
                      className={`p-4 border-2 rounded-lg text-left transition-colors ${
                        labelUploadType === 'unit' 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center mb-2">
                        <FiFileText className="h-5 w-5 text-gray-600 mr-2" />
                        <span className="font-medium">Unit-wise</span>
                      </div>
                      <p className="text-sm text-gray-600">Upload PDF for specific unit</p>
                    </button>
                  </div>
                </div>

                {/* Master Shipment Selection */}
                {labelUploadType === 'master' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Master Shipment
                    </label>
                    <select
                      value={selectedMasterShipment?.id || ''}
                      onChange={(e) => {
                        const shipment = filteredShipments.find(s => s.id === parseInt(e.target.value));
                        setSelectedMasterShipment(shipment);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Select a master shipment</option>
                      {filteredShipments.map(shipment => (
                        <option key={shipment.id} value={shipment.id}>
                          {shipment.master_tracking_number} - {shipment.user?.company_name}
                          {shipment.status === 'proceeded' ? ' (PROCEEDED)' : ''}
                          {shipment.label_file ? ' (HAS MASTER LABEL)' : ''}
                        </option>
                      ))}
                    </select>
                    {selectedMasterShipment && (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                        <p><strong>Status:</strong> {selectedMasterShipment.status}</p>
                        {selectedMasterShipment.label_file && (
                          <p className="text-purple-600"><strong>Master Label:</strong> Already uploaded</p>
                        )}
                        {selectedMasterShipment.boxes?.some(box => box.label_file) && (
                          <p className="text-indigo-600"><strong>Box Labels:</strong> Some boxes have labels</p>
                        )}
                        {selectedMasterShipment.boxes?.some(box => 
                          box.units?.some(unit => unit.label_file)
                        ) && (
                          <p className="text-pink-600"><strong>Unit Labels:</strong> Some units have labels</p>
                        )}
                      </div>
                    )}
                  </div>
                )}


                {/* Unit Selection */}
                {labelUploadType === 'unit' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Master Shipment
                    </label>
                    <select
                      value={selectedMasterShipment?.id || ''}
                      onChange={(e) => {
                        const shipment = filteredShipments.find(s => s.id === parseInt(e.target.value));
                        setSelectedMasterShipment(shipment);
                        setSelectedUnitForLabel(null);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Select a master shipment</option>
                      {filteredShipments.map(shipment => (
                        <option key={shipment.id} value={shipment.id}>
                          {shipment.master_tracking_number} - {shipment.user?.company_name}
                        </option>
                      ))}
                    </select>
                    
                    {selectedMasterShipment && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Unit
                        </label>
                        <select
                          value={selectedUnitForLabel?.id || ''}
                          onChange={(e) => {
                            const unit = selectedMasterShipment.units?.find(u => u.id === parseInt(e.target.value));
                            setSelectedUnitForLabel(unit);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="">Select a unit</option>
                          {selectedMasterShipment.units?.map(unit => (
                            <option key={unit.id} value={unit.id}>
                              {unit.customer_name || 'Unnamed'} - {unit.item_description?.substring(0, 50)}...
                              {unit.carrier_tracking_number ? ` (${unit.carrier_tracking_number})` : ' (No tracking)'}
                              {unit.label_file ? ' (HAS LABEL)' : ''}
                            </option>
                          ))}
                        </select>
                        {selectedUnitForLabel && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                            <p><strong>Unit:</strong> {selectedUnitForLabel.customer_name || 'Unnamed'}</p>
                            <p><strong>Item:</strong> {selectedUnitForLabel.item_description}</p>
                            <p><strong>Tracking:</strong> {selectedUnitForLabel.carrier_tracking_number || 'No tracking number'}</p>
                            {selectedUnitForLabel.label_file && (
                              <p className="text-pink-600"><strong>Unit Label:</strong> Already uploaded</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select PDF Label
                  </label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleLabelFileUpload}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  {labelFile && (
                    <p className="text-sm text-green-600 mt-1">
                      <FiCheckCircle className="h-4 w-4 inline mr-1" />
                      {labelFile.name} selected
                    </p>
                  )}
                </div>

                {/* Upload Summary */}
                {labelFile && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h5 className="font-medium text-gray-800 mb-2">Upload Summary</h5>
                    <div className="text-sm text-gray-600">
                      <p><strong>Type:</strong> {labelUploadType.charAt(0).toUpperCase() + labelUploadType.slice(1)} Label</p>
                      <p><strong>File:</strong> {labelFile.name}</p>
                      {labelUploadType === 'master' && selectedMasterShipment && (
                        <p><strong>Shipment:</strong> {selectedMasterShipment.master_tracking_number}</p>
                      )}
                      {labelUploadType === 'unit' && selectedUnitForLabel && (
                        <>
                          <p><strong>Unit:</strong> {selectedUnitForLabel.customer_name || 'Unnamed'}</p>
                          <p><strong>Item:</strong> {selectedUnitForLabel.item_description}</p>
                          <p><strong>Tracking:</strong> {selectedUnitForLabel.carrier_tracking_number || 'No tracking number'}</p>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Warning for shipments without tracking numbers */}
                {selectedMasterShipment && !allNonCancelledUnitsHaveTracking(selectedMasterShipment.units) && (
                  <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                    <div className="flex">
                      <FiAlertCircle className="h-5 w-5 text-red-400 mr-2" />
                      <div>
                        <h5 className="font-medium text-red-800">Cannot Upload Labels</h5>
                        <p className="text-sm text-red-700 mt-1">
                          This shipment doesn't have tracking numbers for all non-cancelled units. Please upload tracking numbers first before uploading labels. (Cancelled/exception units are excluded from this requirement)
                        </p>
                        <div className="mt-2 text-xs text-red-600">
                          <p>Steps to follow:</p>
                          <ol className="list-decimal list-inside mt-1 space-y-1">
                            <li>Export CSV to get unit IDs</li>
                            <li>Upload tracking numbers CSV</li>
                            <li>Then upload labels</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Warning for already processed shipments */}
                {selectedMasterShipment && selectedMasterShipment.status === 'proceeded' && (
                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                    <div className="flex">
                      <FiAlertTriangle className="h-5 w-5 text-yellow-400 mr-2" />
                      <div>
                        <h5 className="font-medium text-yellow-800">Warning: Shipment Already Processed</h5>
                        <p className="text-sm text-yellow-700 mt-1">
                          This shipment has already been proceeded. Uploading additional labels may not be necessary.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Warning for existing labels */}
                {labelUploadType === 'master' && selectedMasterShipment && selectedMasterShipment.label_file && (
                  <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                    <div className="flex">
                      <FiAlertTriangle className="h-5 w-5 text-orange-400 mr-2" />
                      <div>
                        <h5 className="font-medium text-orange-800">Warning: Master Label Already Exists</h5>
                        <p className="text-sm text-orange-700 mt-1">
                          This shipment already has a master label. Uploading will replace the existing one.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {labelUploadType === 'unit' && selectedUnitForLabel && selectedUnitForLabel.label_file && (
                  <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                    <div className="flex">
                      <FiAlertTriangle className="h-5 w-5 text-orange-400 mr-2" />
                      <div>
                        <h5 className="font-medium text-orange-800">Warning: Unit Label Already Exists</h5>
                        <p className="text-sm text-orange-700 mt-1">
                          This unit already has a label. Uploading will replace the existing one.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowLabelUploadModal(false);
                    setLabelUploadType('master');
                    setSelectedMasterShipment(null);
                    setSelectedUnitForLabel(null);
                    setLabelFile(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={uploadLabel}
                  disabled={
                    !labelFile || 
                    (labelUploadType === 'master' && !selectedMasterShipment) || 
                    (labelUploadType === 'unit' && !selectedUnitForLabel) ||
                    (selectedMasterShipment && !allNonCancelledUnitsHaveTracking(selectedMasterShipment.units))
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiUpload className="h-4 w-4 mr-2 inline" />
                  Upload Label
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proceed Modal */}
      {showProceedModal && selectedShipmentForProceed && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Proceed Shipment: {selectedShipmentForProceed.master_tracking_number}
                </h3>
                <button
                  onClick={() => setShowProceedModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Shipment Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-800 mb-2">Shipment Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-600">Company:</span> {selectedShipmentForProceed.user?.company_name}
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Status:</span> 
                      <span className={`ml-2 px-2 py-1 text-xs rounded ${getStatusColor(selectedShipmentForProceed.status)}`}>
                        {selectedShipmentForProceed.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Boxes:</span> {selectedShipmentForProceed.boxes?.length || 0}
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Total Units:</span> {selectedShipmentForProceed.boxes?.reduce((sum, box) => sum + (box.units?.length || 0), 0) || 0}
                    </div>
                  </div>
                </div>

                {/* Box Tracking Numbers */}
                <div>
                  <h4 className="text-md font-medium text-gray-800 mb-3">Add Tracking Numbers to Boxes</h4>
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Only shipments with child shipment tracking numbers can be proceeded. 
                      Make sure to add tracking numbers to individual units first.
                    </p>
                  </div>
                  <div className="space-y-3">
                    {selectedShipmentForProceed.boxes?.map((box, boxIndex) => {
                      const unitsWithTracking = box.units?.filter(unit => unit.carrier_tracking_number).length || 0;
                      const totalUnits = box.units?.length || 0;
                      return (
                        <div key={box.id} className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="text-sm font-medium text-gray-700">
                              {box.box_name || `Box ${boxIndex + 1}`} ({box.box_tracking_number})
                            </h5>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 text-xs rounded ${getStatusColor(box.status)}`}>
                                {box.status.replace('_', ' ')}
                              </span>
                              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                {unitsWithTracking}/{totalUnits} units tracked
                              </span>
                            </div>
                          </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">
                              Carrier Tracking Number
                            </label>
                            <input
                              type="text"
                              placeholder="e.g., 1Z999AA1234567890"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              onChange={(e) => {
                                // Update the box tracking number in the selected shipment
                                setSelectedShipmentForProceed(prev => ({
                                  ...prev,
                                  boxes: prev.boxes.map((b, index) => 
                                    index === boxIndex ? { ...b, carrier_tracking_number: e.target.value } : b
                                  )
                                }));
                              }}
                              value={box.carrier_tracking_number || ''}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">
                              Carrier
                            </label>
                            <select
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              onChange={(e) => {
                                setSelectedShipmentForProceed(prev => ({
                                  ...prev,
                                  boxes: prev.boxes.map((b, index) => 
                                    index === boxIndex ? { ...b, carrier: e.target.value } : b
                                  )
                                }));
                              }}
                              value={box.carrier || ''}
                            >
                              <option value="">Select Carrier</option>
                              <option value="UPS">UPS</option>
                              <option value="USPS">USPS</option>
                              <option value="FedEx">FedEx</option>
                              <option value="DHL">DHL</option>
                            </select>
                          </div>
                        </div>
                          <div className="mt-2 text-xs text-gray-500">
                            {box.units?.length || 0} units in this box
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowProceedModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={proceedShipment}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                  >
                    <FiCheckCircle className="h-4 w-4 mr-2 inline" />
                    Proceed Shipment
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Box Scan Modal */}
      {showBoxScanModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Receive Box - {warehouseType === 'origin' ? 'Origin Warehouse' : 'Destination Warehouse'}
                </h3>
                <button
                  onClick={() => setShowBoxScanModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                {warehouseType === 'origin' ? (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Enter the Box ID to receive the box. Tracking numbers will be assigned after the box is received.
                    </p>
                  </div>
                ) : (
                  <div className="mb-4 p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-800">
                      <strong>Note:</strong> Enter the Box Tracking Number to receive the box at destination warehouse.
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {warehouseType === 'origin' ? 'Box ID' : 'Box Tracking Number'}
                  </label>
                  <input
                    type="text"
                    value={boxId}
                    onChange={(e) => setBoxId(e.target.value)}
                    placeholder={warehouseType === 'origin' 
                      ? "Enter box ID (e.g., BXA8C87271)" 
                      : "Enter box tracking number (e.g., 1Z999AA1234567890)"
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowBoxScanModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (boxId.trim()) {
                        scanBoxMutation.mutate({
                          boxId: boxId.trim(),
                          warehouseType
                        });
                      }
                    }}
                    disabled={!boxId.trim() || scanBoxMutation.isLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {scanBoxMutation.isLoading ? 'Receiving...' : 'Receive Box'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Box Tracking Upload Modal */}
      {showBoxTrackingModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Upload Box Tracking Numbers
                </h3>
                <button
                  onClick={() => setShowBoxTrackingModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Upload tracking numbers for boxes that are being shipped internationally from India to USA.
                </p>
                
                <div className="space-y-4">
                  {filteredShipments
                    .filter(shipment => shipment.boxes && shipment.boxes.length > 0)
                    .map(shipment => (
                      <div key={shipment.id} className="border rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">
                          {shipment.master_tracking_number}
                        </h4>
                        <div className="space-y-2">
                          {shipment.boxes.map(box => (
                            <div key={box.id} className="flex items-center space-x-4">
                              <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700">
                                  {box.box_name} ({box.box_tracking_number})
                                </label>
                                <div className="flex space-x-2 mt-1">
                                  <input
                                    type="text"
                                    placeholder="Tracking Number"
                                    value={boxTrackingData.find(b => b.box_id === box.id)?.tracking_number || ''}
                                    onChange={(e) => {
                                      const newData = [...boxTrackingData];
                                      const existing = newData.find(b => b.box_id === box.id);
                                      if (existing) {
                                        existing.tracking_number = e.target.value;
                                      } else {
                                        newData.push({
                                          box_id: box.id,
                                          tracking_number: e.target.value,
                                          carrier: 'UPS'
                                        });
                                      }
                                      setBoxTrackingData(newData);
                                    }}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  <select
                                    value={boxTrackingData.find(b => b.box_id === box.id)?.carrier || 'UPS'}
                                    onChange={(e) => {
                                      const newData = [...boxTrackingData];
                                      const existing = newData.find(b => b.box_id === box.id);
                                      if (existing) {
                                        existing.carrier = e.target.value;
                                      } else {
                                        newData.push({
                                          box_id: box.id,
                                          tracking_number: '',
                                          carrier: e.target.value
                                        });
                                      }
                                      setBoxTrackingData(newData);
                                    }}
                                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="UPS">UPS</option>
                                    <option value="FedEx">FedEx</option>
                                    <option value="DHL">DHL</option>
                                    <option value="USPS">USPS</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowBoxTrackingModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const validData = boxTrackingData.filter(b => b.tracking_number.trim());
                      if (validData.length > 0) {
                        uploadBoxTrackingMutation.mutate(validData);
                      }
                    }}
                    disabled={boxTrackingData.filter(b => b.tracking_number.trim()).length === 0 || uploadBoxTrackingMutation.isLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {uploadBoxTrackingMutation.isLoading ? 'Uploading...' : 'Upload Tracking Numbers'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receive Modal (Destination Warehouse) */}
      {showReceiveModal && selectedShipmentForReceive && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Receive Shipment at Destination Warehouse
                </h3>
                <button
                  onClick={() => setShowReceiveModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Shipment Details</h4>
                  <p className="text-sm text-gray-600">
                    <strong>Master ID:</strong> {selectedShipmentForReceive.master_tracking_number}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Company:</strong> {selectedShipmentForReceive.user?.company_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Boxes:</strong> {selectedShipmentForReceive.boxes?.length || 0}
                  </p>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex">
                    <FiAlertCircle className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800">Receiving Process</h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        This will mark the shipment as received at the destination warehouse (USA). 
                        The shipment will be ready for dispatch to customers.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6 space-x-3">
                <button
                  onClick={() => setShowReceiveModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    receiveShipmentMutation.mutate({
                      shipmentId: selectedShipmentForReceive.id,
                      data: {
                        status: 'received_in_usa',
                        boxes: selectedShipmentForReceive.boxes?.map(box => ({
                          id: box.id,
                          status: 'received_in_usa'
                        })) || []
                      }
                    });
                  }}
                  disabled={receiveShipmentMutation.isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {receiveShipmentMutation.isLoading ? 'Receiving...' : 'Receive Shipment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dispatch Modal */}
      {showDispatchModal && selectedShipmentForDispatch && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Dispatch Shipment
                </h3>
                <button
                  onClick={() => setShowDispatchModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Shipment Details</h4>
                  <p className="text-sm text-gray-600">
                    <strong>Master ID:</strong> {selectedShipmentForDispatch.master_tracking_number}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Company:</strong> {selectedShipmentForDispatch.user?.company_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Boxes:</strong> {selectedShipmentForDispatch.boxes?.length || 0}
                  </p>
                </div>
                
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex">
                    <FiTruck className="h-5 w-5 text-orange-400 mr-2 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-orange-800">Dispatch Process</h4>
                      <p className="text-sm text-orange-700 mt-1">
                        This will dispatch the shipment for last-mile delivery. 
                        Customers will see their packages as "In Transit" until delivery.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6 space-x-3">
                <button
                  onClick={() => setShowDispatchModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const dispatchData = {
                      shipmentId: selectedShipmentForDispatch.id,
                      data: {
                        status: 'last_mile_in_transit',
                        boxes: selectedShipmentForDispatch.boxes?.map(box => ({
                          id: box.id,
                          status: 'last_mile_in_transit',
                          units: box.units?.map(unit => ({
                            id: unit.id,
                            status: 'in_transit'
                          })) || []
                        })) || []
                      }
                    };
                    console.log('DISPATCH DEBUG: About to dispatch with data:', dispatchData);
                    dispatchShipmentMutation.mutate(dispatchData);
                  }}
                  disabled={dispatchShipmentMutation.isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50"
                >
                  {dispatchShipmentMutation.isLoading ? 'Dispatching...' : 'Dispatch Shipment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deliver Modal */}
      {showDeliverModal && selectedShipmentForDeliver && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Mark as Delivered
                </h3>
                <button
                  onClick={() => setShowDeliverModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Shipment Details</h4>
                  <p className="text-sm text-gray-600">
                    <strong>Master ID:</strong> {selectedShipmentForDeliver.master_tracking_number}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Company:</strong> {selectedShipmentForDeliver.user?.company_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Boxes:</strong> {selectedShipmentForDeliver.boxes?.length || 0}
                  </p>
                </div>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex">
                    <FiCheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-green-800">Delivery Confirmation</h4>
                      <p className="text-sm text-green-700 mt-1">
                        This will mark the shipment as delivered. 
                        Customers will see their packages as "Delivered" and the shipment will be complete.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6 space-x-3">
                <button
                  onClick={() => setShowDeliverModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    deliverShipmentMutation.mutate({
                      shipmentId: selectedShipmentForDeliver.id,
                      data: {
                        status: 'delivered',
                        boxes: selectedShipmentForDeliver.boxes?.map(box => ({
                          id: box.id,
                          status: 'delivered',
                          units: box.units?.map(unit => ({
                            id: unit.id,
                            status: 'delivered'
                          })) || []
                        })) || []
                      }
                    });
                  }}
                  disabled={deliverShipmentMutation.isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {deliverShipmentMutation.isLoading ? 'Marking...' : 'Mark as Delivered'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Child Shipment Cancellation Modal */}
      {showCancellationModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Cancel Child Shipment</h3>
                <button
                  onClick={() => setShowCancellationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <div className="flex">
                    <FiAlertCircle className="h-5 w-5 text-red-400 mr-2 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-red-800">Cancel Child Shipment</h4>
                      <p className="text-sm text-red-700 mt-1">
                        This will cancel the selected child shipment. The customer will be notified and this item will be excluded from billing.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cancellation Reason *
                  </label>
                  <select
                    value={cancellationForm.reason}
                    onChange={(e) => setCancellationForm(prev => ({ ...prev, reason: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  >
                    <option value="">Select a reason</option>
                    <option value="address_issues">Address Issues</option>
                    <option value="out_of_serviceable_area">Out of Serviceable Area</option>
                    <option value="customer_request">Customer Request</option>
                    <option value="item_damaged">Item Damaged</option>
                    <option value="incorrect_item">Incorrect Item</option>
                    <option value="duplicate_order">Duplicate Order</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                {cancellationForm.reason === 'other' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Please specify
                    </label>
                    <textarea
                      value={cancellationForm.customReason || ''}
                      onChange={(e) => setCancellationForm(prev => ({ ...prev, customReason: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      rows={3}
                      placeholder="Please provide details..."
                    />
                  </div>
                )}
              </div>
              
              <div className="flex justify-end mt-6 space-x-3">
                <button
                  onClick={() => setShowCancellationModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitCancellation}
                  disabled={!cancellationForm.reason || (cancellationForm.reason === 'other' && !cancellationForm.customReason?.trim()) || cancelChildShipmentMutation.isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelChildShipmentMutation.isLoading ? 'Cancelling...' : 'Cancel Shipment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShipments;
